import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Waits for any in-progress MSAL interaction (e.g., redirect callback) to complete,
 * then checks if the user is authenticated. Prevents race condition between MSAL
 * redirect handling and guard evaluation.
 */
export const authGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const broadcastService = inject(MsalBroadcastService);
  const router = inject(Router);

  return broadcastService.inProgress$.pipe(
    filter(status => status === InteractionStatus.None),
    map(() => {
      if (auth.isAuthenticated()) return true;
      return router.createUrlTree(['/auth']);
    }),
  );
};

/**
 * Redirects authenticated users away from the login page.
 */
export const redirectIfAuthenticatedGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const broadcastService = inject(MsalBroadcastService);
  const router = inject(Router);

  return broadcastService.inProgress$.pipe(
    filter(status => status === InteractionStatus.None),
    map(() => {
      if (auth.isAuthenticated()) return router.createUrlTree(['/']);
      return true;
    }),
  );
};

/**
 * Protects admin-only routes. Redirects non-admins to the home page.
 */
export const adminGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const broadcastService = inject(MsalBroadcastService);
  const router = inject(Router);

  return broadcastService.inProgress$.pipe(
    filter(status => status === InteractionStatus.None),
    map(() => {
      if (!auth.isAuthenticated()) return router.createUrlTree(['/auth']);
      if (auth.isAdmin()) return true;
      return router.createUrlTree(['/not-found']);
    }),
  );
};

/**
 * Protects /internal/admin. Only Horizon.Operator users may pass; everyone else is redirected.
 */
export const operatorGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const broadcastService = inject(MsalBroadcastService);
  const router = inject(Router);

  return broadcastService.inProgress$.pipe(
    filter(status => status === InteractionStatus.None),
    map(() => {
      if (!auth.isAuthenticated()) return router.createUrlTree(['/auth']);
      if (auth.isOperator()) return true;
      return router.createUrlTree(['/not-found']);
    }),
  );
};

/**
 * Blocks Horizon.Operator users from regular application routes.
 * Redirects them to /internal/admin; non-operator users pass through.
 */
export const blockOperatorGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const broadcastService = inject(MsalBroadcastService);
  const router = inject(Router);

  return broadcastService.inProgress$.pipe(
    filter(status => status === InteractionStatus.None),
    map(() => {
      if (auth.isOperator()) return router.createUrlTree(['/internal/admin']);
      return true;
    }),
  );
};
